/**
 * **O que um casco leva EM CIMA quando está sob um debuff** (gate V28H.W).
 *
 * Três cartas põem estado noutra nave, e as três têm de se ler de relance - no
 * pior caso futuro, um 3x3 com três cascos do outro lado a levar coisas
 * diferentes ao mesmo tempo. Por isso nenhuma delas é a mesma animação pintada
 * de outra cor: elas diferem na FORMA, no sítio e no ritmo.
 *
 * | Carta | O que se vê | Onde |
 * | ---- | ---- | ---- |
 * | Malware | um símbolo hexagonal com falhas digitais, e uma aura baixa | sobre o casco |
 * | Congelamento | uma casca de cristais, com três espessuras | à volta do casco |
 * | Congelamento total | uma estrela de gelo | ACIMA do casco |
 * | Lentidão | flocos leves que caem e apagam | atrás do casco |
 *
 * ## A cor, e porque ela não é a do jogador
 *
 * Estes quatro **não** usam o selector de cor. Ele existe para os efeitos das
 * cartas de quem joga - o círculo, a serra, o feixe, a chama - e a razão é que
 * eles são a assinatura visual do dono. Um debuff é o contrário: é uma coisa que
 * te aconteceu, e tem de dizer QUAL foi. Magenta e ciano para o Malware - o
 * contrato pede a assinatura digital e proíbe explicitamente o verde ácido do
 * Veneno - e azul-branco para o gelo.
 *
 * ## O que nunca é tapado
 *
 * O casco, a barra de vida, o nickname e a mira. A aura do Malware é baixa por
 * decisão escrita, a casca de gelo desenha-se por dentro do perímetro do casco,
 * e os flocos ficam atrás. Nada disto sobe acima da barra excepto a estrela da
 * paralisia total, que é o único destes estados que muda o que o adversário pode
 * fazer contigo.
 */

import type Phaser from 'phaser';

const TEXTURE = {
  malware: 'card-status-malware',
  malwareAura: 'card-status-malware-aura',
  malwareCircle: 'card-status-malware-circle',
  glow: 'card-status-glow',
  shockPrefix: 'card-status-shock-',
  skull: 'card-status-skull',
  frost: 'card-status-frost',
  paralysis: 'card-status-paralysis',
  flake: 'card-status-flake',
} as const;

/** Quantos flocos a Lentidão traz atrás de si, e quanto cada um vive. */
const FLAKES = 10;
const FLAKE_LIFE_MS = 900;
const FLAKE_INTERVAL_MS = 120;

/**
 * **As cores do Malware, e a do gelo.**
 *
 * O ROXO é a cor da carta (decisão do dono): ele é a única coisa deste tamanho
 * sobre um casco que não é vermelha nem verde, e é isso que o separa das garras
 * do Sangramento e das gotas do Veneno num relance. O ciano entra só nas falhas
 * - o desvio de canal de um sinal corrompido - e o magenta ficou para a aura, em
 * baixo, onde nada tem de ser lido.
 */
const MALWARE_VIOLET = 0xc0_6a_ff;
const MALWARE_MAGENTA = 0xff_4b_d8;
const MALWARE_CYAN = 0x4b_e8_ff;
const ICE_TINT = 0xcf_ec_ff;

/**
 * **A cor da descarga: branco-quente, e não azul** (decisão do dono, V28H.X).
 *
 * "O efeito da habilidade é branco/amarelado, tem que ser da mesma cor": o raio
 * que voa é branco com um halo pálido, e o que ele deixa no casco tem de ser
 * reconhecível como a mesma coisa. Um azul eléctrico era bonito e era outra
 * carta.
 */
const SHOCK_TINT = 0xff_f4_d2;

/** Quantas chapas tem a descarga, e como se chama cada uma. */
const SHOCK_VARIANTS = 3;

/** Trocadas depressa: é a troca que faz a electricidade saltar. */
const SHOCK_FRAME_MS = 70;

function shockTextureKey(variant: number): string {
  return `${TEXTURE.shockPrefix}${variant}`;
}
/**
 * **Vermelho intenso, e não rosa** (decisão do dono, gate V28H.X).
 *
 * O primeiro tom era claro para a caveira se ler sobre o casco escuro; acima do
 * nome, com um halo por trás, ela não precisa disso - e o que se lia era rosa.
 * Este é o vermelho de um aviso.
 */
const SKULL_TINT = 0xff_0e_0e;

/**
 * **O ritmo da falha**: um estalo curto a cada ciclo, e não cintilação contínua.
 *
 * Derivado do relógio da cena e não de um temporizador próprio, portanto duas
 * naves infectadas ao mesmo tempo falham ao mesmo tempo - é o mesmo sinal, e
 * dois ritmos por acaso diferentes leriam-se como duas coisas.
 */
const GLITCH_CYCLE_MS = 900;
const GLITCH_BURST_MS = 150;

/**
 * Um gerador determinista, para espalhar os flocos sem `Math.random`.
 *
 * A mesma razão de sempre: duas abas com o mesmo relógio desenham o mesmo, e uma
 * captura de ecrã compara-se com a seguinte.
 */
function hash(seed: number): number {
  let x = (seed * 1_664_525 + 1_013_904_223) % 4_294_967_296;
  x = ((x ^ (x >>> 15)) * 2_246_822_519) % 4_294_967_296;
  return ((x ^ (x >>> 13)) % 100_000) / 100_000;
}

/**
 * **O selo do Malware**: um escudo hexagonal partido, com um circuito dentro.
 *
 * Ele vive ACIMA da barra de vida (decisão do dono) e por isso tem de aguentar
 * a comparação com o que já mora nessa faixa: as gotas do Veneno e as garras do
 * Sangramento. As três coisas separam-se por tudo ao mesmo tempo - o Veneno são
 * gotas verdes coladas à barra, o Sangramento são riscos vermelhos curtos, e
 * isto é um SELO roxo, hexagonal, com um contorno fechado e um símbolo lá
 * dentro. Nenhuma das outras duas tem contorno nem interior.
 *
 * O circuito é o que faz o selo dizer "sistema comprometido" em vez de dizer
 * apenas "algo mau": um tronco vertical, duas derivações em ângulo recto e três
 * terminais - a forma que qualquer pessoa lê como um chip.
 *
 * Branco por baixo, sempre: a cor entra pela tinta de quem desenha, que é o que
 * permite a mesma textura servir o selo roxo e o fantasma ciano da falha.
 */
function makeMalware(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 128;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const radius = middle - 9;
  const hexagon = (scale: number) => {
    context.beginPath();
    for (let index = 0; index < 6; index += 1) {
      const angle = (index / 6) * Math.PI * 2 - Math.PI / 2;
      const x = middle + Math.cos(angle) * radius * scale;
      const y = middle + Math.sin(angle) * radius * scale;
      if (index === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    }
    context.closePath();
  };
  /* O CORPO: um interior fraco, para o selo ter massa e não ser um arame. */
  hexagon(1);
  context.fillStyle = 'rgba(255,255,255,0.22)';
  context.fill();
  /* O CONTORNO, grosso e fechado: é ele que faz isto ler-se como um selo. */
  context.strokeStyle = 'rgba(255,255,255,0.98)';
  context.lineWidth = 8;
  hexagon(1);
  context.stroke();

  /*
   * O CIRCUITO. Traços rectos e ângulos rectos - nada aqui é curvo, e é
   * deliberado: tudo o resto que este jogo põe sobre um casco é orgânico.
   */
  context.strokeStyle = 'rgba(255,255,255,0.95)';
  context.lineWidth = 5;
  context.lineCap = 'square';
  context.beginPath();
  context.moveTo(middle, middle - radius * 0.52);
  context.lineTo(middle, middle + radius * 0.5);
  context.moveTo(middle, middle - radius * 0.18);
  context.lineTo(middle + radius * 0.42, middle - radius * 0.18);
  context.lineTo(middle + radius * 0.42, middle + radius * 0.16);
  context.moveTo(middle, middle + radius * 0.2);
  context.lineTo(middle - radius * 0.42, middle + radius * 0.2);
  context.stroke();
  /* Os TERMINAIS: três pontos, nas três pontas do circuito. */
  context.fillStyle = 'rgba(255,255,255,1)';
  for (const [x, y] of [
    [middle, middle - radius * 0.52],
    [middle + radius * 0.42, middle + radius * 0.16],
    [middle - radius * 0.42, middle + radius * 0.2],
  ] as const) {
    context.beginPath();
    context.arc(x, y, 5.5, 0, Math.PI * 2);
    context.fill();
  }

  /* E as FALHAS: três faixas comidas ao selo, como um sinal corrompido. */
  context.globalCompositeOperation = 'destination-out';
  for (const [y, height] of [
    [middle - radius * 0.62, 6],
    [middle - radius * 0.02, 5],
    [middle + radius * 0.58, 7],
  ] as const) {
    context.fillRect(0, y, size, height);
  }
  context.globalCompositeOperation = 'source-over';
  texture.refresh();
}

/**
 * **A aura do Malware**: um halo suave, e não o selo esticado.
 *
 * Antes a aura era a própria textura do selo ampliada, e via-se: um hexágono
 * gigante e desbotado à volta do casco, com o contorno todo. Um halo tem de ser
 * uma mancha - sem forma reconhecível, sem arestas - senão ele compete com o
 * sinal que está a acompanhar em vez de o sustentar.
 *
 * Vazio no centro por construção: o que ele emoldura é o casco, e o contrato
 * escreve que nada pode escondê-lo.
 */
function makeMalwareAura(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 160;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const halo = context.createRadialGradient(middle, middle, 0, middle, middle, middle);
  halo.addColorStop(0, 'rgba(255,255,255,0)');
  halo.addColorStop(0.52, 'rgba(255,255,255,0)');
  halo.addColorStop(0.74, 'rgba(255,255,255,0.5)');
  halo.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = halo;
  context.fillRect(0, 0, size, size);
  texture.refresh();
}

/**
 * **A casca de gelo**: um anel de cristais rectos, virados para fora.
 *
 * Cristais e não um disco azul, e é a diferença que o contrato exige - "o alvo
 * deve parecer congelado, não apenas pintado de azul". Eles apontam para fora a
 * partir de uma coroa, portanto a silhueta da nave continua a ver-se por dentro
 * deles.
 */
function makeFrost(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 256;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const inner = middle * 0.62;
  const outer = middle - 6;
  for (let index = 0; index < 14; index += 1) {
    const angle = (index / 14) * Math.PI * 2;
    const half = (Math.PI / 14) * (0.5 + hash(index * 3) * 0.4);
    const reach = inner + (outer - inner) * (0.5 + hash(index * 3 + 1) * 0.5);
    context.fillStyle = index % 2 === 0 ? 'rgba(255,255,255,1)' : 'rgba(206,238,255,0.88)';
    context.beginPath();
    context.moveTo(
      middle + Math.cos(angle - half) * inner,
      middle + Math.sin(angle - half) * inner,
    );
    context.lineTo(middle + Math.cos(angle) * reach, middle + Math.sin(angle) * reach);
    context.lineTo(
      middle + Math.cos(angle + half) * inner,
      middle + Math.sin(angle + half) * inner,
    );
    context.closePath();
    context.fill();
    /*
     * **E o contorno de cada cristal** (gate V28H.W): sem ele os catorze são uma
     * mancha clara e única, e o dono pediu nitidez. Uma aresta desenhada é o que
     * faz um cristal ler-se como cristal a qualquer tamanho.
     */
    context.strokeStyle = 'rgba(255,255,255,0.95)';
    context.lineWidth = 2.5;
    context.stroke();
  }
  /* E a coroa que os une: sem ela os cristais flutuam soltos. */
  context.strokeStyle = 'rgba(226,246,255,0.8)';
  context.lineWidth = 5;
  context.beginPath();
  context.arc(middle, middle, inner, 0, Math.PI * 2);
  context.stroke();
  texture.refresh();
}

/**
 * **A estrela da paralisia total**: quatro pontas longas e um brilho.
 *
 * Ela aparece ACIMA da nave e só quando o ataque também está travado - "essa
 * estrela representa paralisia total e não deve aparecer quando apenas o
 * movimento estiver bloqueado". Quatro pontas e não seis, para não se confundir
 * com o floco da Lentidão nem com a estrela que a Lentidão lança.
 */
function makeParalysis(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 128;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const long = middle - 6;
  const short = long * 0.22;
  context.fillStyle = 'rgba(255,255,255,0.95)';
  context.beginPath();
  for (let index = 0; index < 8; index += 1) {
    const radius = index % 2 === 0 ? long : short;
    const angle = (index / 8) * Math.PI * 2 - Math.PI / 2;
    const x = middle + Math.cos(angle) * radius;
    const y = middle + Math.sin(angle) * radius;
    if (index === 0) {
      context.moveTo(x, y);
    } else {
      context.lineTo(x, y);
    }
  }
  context.closePath();
  context.fill();
  const glow = context.createRadialGradient(middle, middle, 0, middle, middle, long * 0.5);
  glow.addColorStop(0, 'rgba(255,255,255,0.9)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = glow;
  context.fillRect(0, 0, size, size);
  texture.refresh();
}

/** O floco da Lentidão: pequeno, de seis braços, e nada mais. */
function makeFlake(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 64;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  context.strokeStyle = 'rgba(255,255,255,0.92)';
  context.lineWidth = 4;
  context.lineCap = 'round';
  for (let index = 0; index < 3; index += 1) {
    const angle = (index / 3) * Math.PI;
    context.beginPath();
    context.moveTo(
      middle - Math.cos(angle) * (middle - 6),
      middle - Math.sin(angle) * (middle - 6),
    );
    context.lineTo(
      middle + Math.cos(angle) * (middle - 6),
      middle + Math.sin(angle) * (middle - 6),
    );
    context.stroke();
  }
  texture.refresh();
}

/**
 * **O círculo mágico do Malware** (decisão do dono, gate V28H.W).
 *
 * Um selo de invocação, deitado por baixo da nave infectada: dois anéis
 * concêntricos, uma coroa de runas rectas entre eles e dois triângulos cruzados
 * lá dentro. Roxo néon pela tinta de quem o desenha.
 *
 * ## Porque ele é vazio no meio
 *
 * Porque a nave está lá. O contrato escreve que nada pode esconder o casco, a
 * barra ou a mira, e um disco preenchido por baixo de uma nave escura fá-la
 * desaparecer - isto tem de ser um ANEL, com o interior a deixar passar o fundo
 * e o casco.
 *
 * ## Porque as runas são traços e não símbolos
 *
 * Porque à escala a que isto se vê - pouco mais de um casco de diâmetro, num
 * ecrã que pode ter mais duas naves - um símbolo desenhado lê-se como sujidade.
 * Traços rectos de comprimentos alternados dão a mesma leitura de "selo" e
 * sobrevivem a ser pequenos.
 */
function makeMalwareCircle(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 256;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  /* Margem para o BORRÃO do néon caber: sem ela o halo é cortado na borda. */
  const outer = middle - 24;
  const inner = outer * 0.72;
  /*
   * **NÉON, e néon é duas camadas** (decisão do dono, gate V28H.W).
   *
   * O selo estava desenhado a traço simples e o dono disse-o em duas palavras:
   * "não está néon". Um tubo de néon não é uma linha colorida - é um núcleo
   * quase branco com um halo largo e difuso da cor à volta, e o olho lê o halo
   * antes de ler a linha.
   *
   * Aqui isso faz-se com `shadowBlur`: cada traço é pintado DUAS vezes, uma
   * larga e desfocada e outra fina e sólida por cima. Assado na textura em vez
   * de somado com um segundo sprite - uma textura custa uma vez, um sprite custa
   * todos os quadros, e o resultado no ecrã é o mesmo.
   */
  const neon = (draw: () => void, width: number, alpha: number) => {
    context.save();
    context.shadowColor = 'rgba(255,255,255,0.95)';
    context.shadowBlur = 16;
    context.strokeStyle = `rgba(255,255,255,${alpha * 0.55})`;
    context.lineWidth = width * 2.4;
    draw();
    context.shadowBlur = 8;
    context.strokeStyle = `rgba(255,255,255,${alpha})`;
    context.lineWidth = width;
    draw();
    context.restore();
  };
  const ring = (radius: number, width: number, alpha: number) => {
    neon(
      () => {
        context.beginPath();
        context.arc(middle, middle, radius, 0, Math.PI * 2);
        context.stroke();
      },
      width,
      alpha,
    );
  };
  ring(outer, 5, 1);
  ring(inner, 3.5, 0.9);
  ring(inner * 0.5, 2.5, 0.6);
  /* As RUNAS: traços rectos entre os dois anéis, de comprimentos alternados. */
  context.lineCap = 'butt';
  for (let index = 0; index < 24; index += 1) {
    const angle = (index / 24) * Math.PI * 2;
    const long = index % 2 === 0;
    const from = inner + (outer - inner) * (long ? 0.12 : 0.35);
    const to = outer - (outer - inner) * (long ? 0.12 : 0.35);
    neon(
      () => {
        context.beginPath();
        context.moveTo(middle + Math.cos(angle) * from, middle + Math.sin(angle) * from);
        context.lineTo(middle + Math.cos(angle) * to, middle + Math.sin(angle) * to);
        context.stroke();
      },
      long ? 4 : 2.5,
      long ? 1 : 0.7,
    );
  }
  /* E os dois triângulos cruzados, que é o que faz isto ler-se como SELO. */
  for (const turn of [0, Math.PI]) {
    neon(
      () => {
        context.beginPath();
        for (let index = 0; index < 3; index += 1) {
          const angle = turn + (index / 3) * Math.PI * 2 - Math.PI / 2;
          const x = middle + Math.cos(angle) * inner * 0.86;
          const y = middle + Math.sin(angle) * inner * 0.86;
          if (index === 0) {
            context.moveTo(x, y);
          } else {
            context.lineTo(x, y);
          }
        }
        context.closePath();
        context.stroke();
      },
      3,
      0.9,
    );
  }
  texture.refresh();
}

/**
 * **A descarga do Curto-Circuito, PELO CORPO da nave** (gate V28H.X).
 *
 * ## O que mudou, e porquê
 *
 * Isto era um anel de três arcos à volta do casco, azul. O dono viu-o e corrigiu
 * as duas coisas: "a electricidade não deve formar um círculo ao redor da nave
 * atingida e sim pela nave toda, pelo corpo da nave", e "o efeito da habilidade
 * é branco/amarelado, tem que ser da mesma cor".
 *
 * Tem razão nas duas. Um anel à volta é uma AURA - a mesma leitura da casca de
 * gelo, que quer dizer outra coisa - e o que uma descarga faz a uma máquina é
 * correr POR ela: saltar de chapa em chapa, de um ponto ao outro do casco.
 *
 * ## Três chapas, e não uma
 *
 * Uma descarga não se repete. Cada chapa tem os seus riscos noutros sítios, e
 * quem desenha troca-as depressa: o que se vê é electricidade a saltar, e não
 * um desenho a rodar. Três chegam - com quatro riscos cada uma, são doze
 * posições, e a esta velocidade ninguém conta o ciclo.
 *
 * Os riscos ficam DENTRO do disco do casco, e é a diferença toda: eles cruzam a
 * nave em vez de a contornar.
 */
function makeShock(scene: Phaser.Scene, key: string, variant: number): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 256;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const radius = middle - 18;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  for (let bolt = 0; bolt < 4; bolt += 1) {
    const seed = variant * 97 + bolt * 31;
    /*
     * Cada risco vai de um ponto do casco a outro, PELO MEIO dele: os dois
     * extremos vivem no disco, e a linha que os une atravessa a nave.
     */
    const fromAngle = hash(seed) * Math.PI * 2;
    const toAngle = fromAngle + Math.PI * (0.55 + hash(seed + 1) * 0.7);
    const fromR = radius * (0.55 + hash(seed + 2) * 0.45);
    const toR = radius * (0.55 + hash(seed + 3) * 0.45);
    const ax = middle + Math.cos(fromAngle) * fromR;
    const ay = middle + Math.sin(fromAngle) * fromR;
    const bx = middle + Math.cos(toAngle) * toR;
    const by = middle + Math.sin(toAngle) * toR;
    const draw = () => {
      context.beginPath();
      for (let step = 0; step <= 6; step += 1) {
        const t = step / 6;
        /* A quebra é perpendicular ao risco, e maior no meio dele. */
        const swing = Math.sin(t * Math.PI) * radius * 0.16;
        const nx = -(by - ay);
        const ny = bx - ax;
        const length = Math.hypot(nx, ny) || 1;
        const offset = (hash(seed * 7 + step) * 2 - 1) * swing;
        const x = ax + (bx - ax) * t + (nx / length) * offset;
        const y = ay + (by - ay) * t + (ny / length) * offset;
        if (step === 0) {
          context.moveTo(x, y);
        } else {
          context.lineTo(x, y);
        }
      }
      context.stroke();
    };
    context.strokeStyle = 'rgba(255,255,255,0.26)';
    context.lineWidth = 13;
    draw();
    context.strokeStyle = 'rgba(255,255,255,1)';
    context.lineWidth = 4.5;
    draw();
    /* E um ponto aceso em cada extremidade: é onde ela toca a chapa. */
    context.fillStyle = 'rgba(255,255,255,0.9)';
    for (const [x, y] of [
      [ax, ay],
      [bx, by],
    ] as const) {
      context.beginPath();
      context.arc(x, y, 4.5, 0, Math.PI * 2);
      context.fill();
    }
  }
  texture.refresh();
}

/**
 * **A caveira da Marca** (gate V28H.X): um crânio, e um crânio MAU.
 *
 * A primeira era simétrica e redonda, e lia-se como um ícone de aviso. O dono
 * pediu-a "mais horripilante": o que a muda não é o tamanho, são as decisões de
 * desenho que um crânio ameaçador tem e um ícone não.
 *
 * - as ÓRBITAS são inclinadas para dentro, e é isso que faz um crânio parecer
 *   zangado - o mesmo truque de uma sobrancelha franzida;
 * - elas são FUNDAS: comidas ao osso com um degradê por dentro, e não dois
 *   buracos chapados;
 * - o maxilar é mais estreito do que a cúpula, e os DENTES têm falhas - um
 *   sorriso completo é simpático;
 * - uma fenda atravessa a testa, e é o que diz que este crânio já apanhou.
 *
 * Branca por baixo, para a tinta de quem desenha a pintar. E desenhada com
 * §shadowBlur§ nas passagens de fora: o halo néon vem já assado na chapa, e não
 * de um segundo sprite a fingir profundidade.
 */
function makeSkull(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 256;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;

  /* O CRÂNIO: cúpula larga, têmporas marcadas, maxilar estreito. */
  const skull = () => {
    context.beginPath();
    context.moveTo(middle - size * 0.3, middle - size * 0.04);
    context.bezierCurveTo(
      middle - size * 0.33,
      middle - size * 0.34,
      middle - size * 0.16,
      middle - size * 0.43,
      middle,
      middle - size * 0.43,
    );
    context.bezierCurveTo(
      middle + size * 0.16,
      middle - size * 0.43,
      middle + size * 0.33,
      middle - size * 0.34,
      middle + size * 0.3,
      middle - size * 0.04,
    );
    /* As maçãs do rosto, e o degrau para o maxilar. */
    context.lineTo(middle + size * 0.24, middle + size * 0.1);
    context.lineTo(middle + size * 0.17, middle + size * 0.13);
    context.lineTo(middle + size * 0.17, middle + size * 0.3);
    context.lineTo(middle - size * 0.17, middle + size * 0.3);
    context.lineTo(middle - size * 0.17, middle + size * 0.13);
    context.lineTo(middle - size * 0.24, middle + size * 0.1);
    context.closePath();
  };

  /* Duas passagens: o halo assado, e o osso sólido por cima dele. */
  context.save();
  context.shadowColor = 'rgba(255,255,255,0.95)';
  context.shadowBlur = 26;
  context.fillStyle = 'rgba(255,255,255,0.55)';
  skull();
  context.fill();
  context.restore();
  context.fillStyle = 'rgba(255,255,255,0.97)';
  skull();
  context.fill();

  /* As ÓRBITAS, inclinadas e fundas. */
  context.globalCompositeOperation = 'destination-out';
  for (const side of [-1, 1] as const) {
    context.save();
    context.translate(middle + side * size * 0.145, middle - size * 0.08);
    /* Inclinadas para dentro: é a sobrancelha franzida de um crânio. */
    context.rotate(side * 0.34);
    context.beginPath();
    context.ellipse(0, 0, size * 0.115, size * 0.145, 0, 0, Math.PI * 2);
    context.fill();
    context.restore();
  }
  /* O nariz: um triângulo invertido, estreito e alto. */
  context.beginPath();
  context.moveTo(middle, middle - size * 0.02);
  context.lineTo(middle + size * 0.055, middle + size * 0.115);
  context.lineTo(middle - size * 0.055, middle + size * 0.115);
  context.closePath();
  context.fill();
  /* A linha da boca, e as falhas entre os dentes. */
  context.fillRect(middle - size * 0.17, middle + size * 0.155, size * 0.34, size * 0.022);
  for (const at of [-0.115, -0.058, 0, 0.058, 0.115]) {
    context.fillRect(middle + size * at - 1.6, middle + size * 0.155, 3.2, size * 0.145);
  }
  /* E a FENDA na testa: este crânio já apanhou. */
  context.save();
  context.strokeStyle = 'rgba(0,0,0,1)';
  context.lineWidth = 3.4;
  context.beginPath();
  context.moveTo(middle + size * 0.02, middle - size * 0.42);
  context.lineTo(middle - size * 0.06, middle - size * 0.3);
  context.lineTo(middle + size * 0.02, middle - size * 0.24);
  context.lineTo(middle - size * 0.03, middle - size * 0.17);
  context.stroke();
  context.restore();
  context.globalCompositeOperation = 'source-over';
  texture.refresh();
}

/**
 * **Um halo redondo e macio**, para pôr por trás de um sinal (gate V28H.X).
 *
 * A caveira tinha por trás uma CÓPIA de si própria, ampliada e somada, e o
 * dono viu o que isso é de facto: "parece duas caveiras sobrepostas". Uma cópia
 * maior de uma silhueta continua a ser uma silhueta - o que faz um halo é não
 * ter forma nenhuma.
 */
function makeGlow(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 256;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const halo = context.createRadialGradient(middle, middle, 0, middle, middle, middle);
  halo.addColorStop(0, 'rgba(255,255,255,0.75)');
  halo.addColorStop(0.34, 'rgba(255,255,255,0.34)');
  halo.addColorStop(0.72, 'rgba(255,255,255,0.09)');
  halo.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = halo;
  context.fillRect(0, 0, size, size);
  texture.refresh();
}

/** Gera as texturas das cartas de estado, uma vez por cena. Idempotente. */
export function ensureStatusTextures(scene: Phaser.Scene): void {
  makeMalware(scene, TEXTURE.malware);
  makeMalwareAura(scene, TEXTURE.malwareAura);
  makeMalwareCircle(scene, TEXTURE.malwareCircle);
  for (let variant = 0; variant < SHOCK_VARIANTS; variant += 1) {
    makeShock(scene, shockTextureKey(variant), variant);
  }
  makeSkull(scene, TEXTURE.skull);
  makeGlow(scene, TEXTURE.glow);
  makeFrost(scene, TEXTURE.frost);
  makeParalysis(scene, TEXTURE.paralysis);
  makeFlake(scene, TEXTURE.flake);
}

/** O que a sala diz sobre este casco, e nada mais do que isto. */
export interface StatusFxState {
  readonly x: number;
  readonly y: number;
  /** O raio do casco desenhado: tudo aqui é medido a partir dele. */
  readonly hullRadius: number;
  /** Quanto o nome e a barra estão acima do casco, para nada os tapar. */
  readonly lift: number;
  /** E quanto o NOME ocupa acima disso: o selo e a estrela empilham-se sobre ele. */
  readonly nameHeight: number;
  readonly malware: boolean;
  /** Zero para nenhuma casca; um, dois ou três para as três espessuras. */
  readonly frostTier: number;
  readonly paralysed: boolean;
  readonly slowed: boolean;
  /** **Os dois do gate V28H.X**: a cadência partida e a marca. */
  readonly attackBroken: boolean;
  readonly marked: boolean;
}

/**
 * **Os quatro sinais de UM casco.**
 *
 * Uma instância por cadeira. Os objectos nascem à primeira vez que fazem falta e
 * depois são **escondidos** em vez de destruídos - estes estados duram segundos
 * e voltam - e saem de vez com o participante.
 */
export class StatusFx {
  private readonly scene: Phaser.Scene;
  private readonly blendAdd: number;
  private readonly depth: number;
  /**
   * **A profundidade dos sinais que vivem ACIMA DA BARRA** (gate V28H.W).
   *
   * Duas e não uma, porque estes objectos moram em dois sítios: a aura, a casca
   * de gelo e os flocos ficam à volta do casco, e o selo do Malware e a estrela
   * da paralisia ficam por cima da barra e do nickname. O selo saiu ATRÁS do
   * nickname na primeira montagem por isto mesmo - ele herdava a profundidade do
   * casco, e o nickname desenha-se na camada da barra, que é mais alta.
   */
  private readonly barDepth: number;
  /**
   * **E a profundidade do CHÃO**, por baixo do casco (gate V28H.W).
   *
   * O círculo mágico e a aura vivem aqui. Eles nasceram na camada da identidade
   * - que é ACIMA da nave - e o dono viu-os sobrepostos ao desenho dela: um selo
   * deitado no chão que passa por cima do que está pousado nele lê-se como um
   * autocolante na lente, e não como uma coisa que está no mundo.
   */
  private readonly groundDepth: number;
  private malware: Phaser.GameObjects.Image | null = null;
  /** A cópia deslocada do selo, que só existe durante a falha (gate V28H.W). */
  private ghost: Phaser.GameObjects.Image | null = null;
  private aura: Phaser.GameObjects.Image | null = null;
  /** O círculo mágico por baixo do casco infectado (gate V28H.W). */
  private circle: Phaser.GameObjects.Image | null = null;
  private frost: Phaser.GameObjects.Image | null = null;
  private paralysis: Phaser.GameObjects.Image | null = null;
  private flakes: Phaser.GameObjects.Image[] = [];
  /** Os dois sinais do gate V28H.X: o arco eléctrico e a caveira. */
  private shock: Phaser.GameObjects.Image | null = null;
  private skull: Phaser.GameObjects.Image | null = null;
  /** O halo somado por trás da caveira: é ele que a faz arder (gate V28H.X). */
  private skullGlow: Phaser.GameObjects.Image | null = null;
  /** Onde cada floco caiu, no MUNDO: eles ficam para trás, a nave não. */
  private readonly snow: { x: number; y: number; bornAt: number; size: number }[] = [];
  private nextFlake = 0;
  private lastFlakeAt = 0;

  constructor(
    scene: Phaser.Scene,
    blendAdd: number,
    depth: number,
    barDepth: number,
    groundDepth: number,
  ) {
    this.scene = scene;
    this.blendAdd = blendAdd;
    this.depth = depth;
    this.barDepth = barDepth;
    this.groundDepth = groundDepth;
  }

  /**
   * **Onde assenta o próximo sinal ACIMA do nome** (gate V28H.X).
   *
   * Três coisas podem viver nesta faixa - o selo do Malware, a caveira da Marca
   * e a estrela da paralisia total - e um casco pode ter as três ao mesmo tempo.
   * Uma pilha decide a ordem uma vez; três números afinados à mão acabariam
   * sempre por se sobrepor no dia em que um deles mudasse de tamanho.
   *
   * O fundo da pilha é o TOPO DO NICKNAME, que já está acima da barra e das
   * faixas do veneno e do sangramento - elas crescem a partir do topo da barra
   * e ficam por baixo do nome. É o que cumpre "sem deixar sobrepor marcas de
   * veneno, sangramento etc.".
   */
  private stackTop = 0;

  /** Reserva espaço para um sinal e devolve o CENTRO dele. */
  private stack(height: number): number {
    const centre = this.stackTop - height * 0.5;
    this.stackTop -= height * 1.12;
    return centre;
  }

  update(state: StatusFxState | null, nowMs: number): void {
    if (state === null) {
      this.hide();
      this.advanceSnow(nowMs);
      return;
    }
    if (
      !state.malware &&
      state.frostTier <= 0 &&
      !state.paralysed &&
      !state.slowed &&
      !state.attackBroken &&
      !state.marked
    ) {
      this.hide();
      this.advanceSnow(nowMs);
      return;
    }
    this.build();
    /* A pilha recomeça em cada quadro, do topo do nome para cima. */
    this.stackTop = state.y - state.lift - state.nameHeight;

    /*
     * **O MALWARE**: o selo ACIMA DA BARRA, e uma aura baixa em volta do casco.
     *
     * ## Porque ele subiu para cima da barra (decisão do dono)
     *
     * Ele estava sobre o casco, onde não competia com nada mas também não se
     * lia: um símbolo em cima da nave é uma coisa que se descobre depois de
     * procurar, e este tem de ser a primeira coisa que se vê num casco
     * infectado, porque muda a conta de quem decide curar-se.
     *
     * Acima da barra, e acima do NOME - `lift` já traz os dois - portanto ele
     * não pisa a faixa das gotas do Veneno nem a das garras do Sangramento, que
     * crescem para cima a partir do topo da barra e ficam por baixo dele.
     *
     * ## A falha, e porque ela não é um piscar
     *
     * De `GLITCH_CYCLE_MS` em `GLITCH_CYCLE_MS` o selo estala: uma cópia ciano
     * salta para o lado e o original treme. É o desvio de canal de um sinal
     * corrompido, dura um sexto do ciclo, e o resto do tempo o selo está parado
     * e legível - que é a diferença entre uma assinatura digital e a cintilação
     * agressiva que o contrato proíbe.
     */
    if (this.malware && this.aura && this.ghost && this.circle) {
      if (!state.malware) {
        this.malware.setVisible(false);
        this.aura.setVisible(false);
        this.ghost.setVisible(false);
        this.circle.setVisible(false);
      } else {
        const phase = nowMs % GLITCH_CYCLE_MS;
        const glitching = phase < GLITCH_BURST_MS;
        /* Zero fora da falha, e um triângulo curto dentro dela. */
        const burst = glitching ? 1 - Math.abs(phase / GLITCH_BURST_MS - 0.5) * 2 : 0;
        const badge = state.hullRadius * 0.66;
        /*
         * O topo do nickname, e não um palpite: o texto tem origem em baixo, e
         * o selo assenta em cima dele com uma folga do seu próprio tamanho.
         * Assim ele continua a não tocar no nome quando o casco for grande e
         * tudo crescer com ele.
         */
        const seal = this.stack(badge);
        /* Um empurrão lateral determinista: para a direita, depois para a esquerda. */
        const slip =
          burst * badge * 0.22 * (Math.floor(nowMs / GLITCH_CYCLE_MS) % 2 === 0 ? 1 : -1);
        this.ghost
          .setVisible(burst > 0)
          .setPosition(state.x - slip, seal + burst * badge * 0.06)
          .setDisplaySize(badge, badge)
          .setTint(MALWARE_CYAN)
          .setAlpha(burst * 0.55);
        this.malware
          .setVisible(true)
          .setPosition(state.x + slip * 0.5, seal)
          .setDisplaySize(badge, badge)
          .setTint(MALWARE_VIOLET)
          .setAlpha(0.86 + Math.sin(nowMs / 520) * 0.12 - burst * 0.18);
        /* E a aura, baixa: ela emoldura o casco e nunca o esconde. */
        this.aura
          .setVisible(true)
          .setPosition(state.x, state.y)
          .setDisplaySize(state.hullRadius * 2.9, state.hullRadius * 2.9)
          .setTint(burst > 0 ? MALWARE_MAGENTA : MALWARE_VIOLET)
          .setAlpha(0.15 + burst * 0.08);
        /*
         * **E o círculo mágico, deitado por baixo da nave** (decisão do dono).
         *
         * Ele RODA devagar, e é a roda que o faz ler-se como um selo activo em
         * vez de um decalque - meia volta em vinte segundos, lento o suficiente
         * para não puxar o olho de um combate. Sob o casco, com o interior
         * vazio: a nave vê-se por dentro dele, que é o que o contrato exige.
         *
         * O magenta entra só no estalo, ao mesmo tempo que o do selo lá em cima:
         * as duas pontas da carta piscam juntas porque são a mesma carta.
         */
        /* O DOBRO do que era (decisão do dono): um selo pequeno não se lê. */
        const sigil = state.hullRadius * 5.2;
        this.circle
          .setVisible(true)
          .setPosition(state.x, state.y)
          .setDisplaySize(sigil, sigil)
          .setTint(burst > 0 ? MALWARE_MAGENTA : MALWARE_VIOLET)
          .setAlpha(0.78 + Math.sin(nowMs / 620) * 0.12 + burst * 0.22)
          .setRotation(nowMs / 6_400);
      }
    }

    /*
     * **O GELO**: uma casca por dentro do perímetro, com três espessuras.
     *
     * O nível vem da sala - um por segundo restante - e o que ele muda é quanto
     * do casco a coroa cobre e com que opacidade. A silhueta continua a ver-se
     * porque os cristais apontam para fora a partir de uma coroa vazia.
     */
    if (this.frost) {
      if (state.frostTier <= 0) {
        this.frost.setVisible(false);
      } else {
        const tier = Math.min(3, Math.max(1, Math.round(state.frostTier)));
        /*
         * **Três vezes maior** (decisão do dono, gate V28H.W).
         *
         * A casca é um ANEL com o meio vazio - os cristais apontam para fora a
         * partir de uma coroa - portanto crescer não tapa a nave: aumenta o
         * diâmetro da coroa e deixa o casco a ver-se por dentro dela na mesma.
         */
        const span = state.hullRadius * (1.75 + tier * 0.26) * 3;
        this.frost
          .setVisible(true)
          .setPosition(state.x, state.y)
          .setDisplaySize(span, span)
          .setTint(ICE_TINT)
          /*
           * **Densa** (gate V28H.W): começava a 0,46 e a casca lia-se como uma
           * sugestão. Ela tem de dizer que aquela nave está PRESA, e três
           * espessuras que se distinguem umas das outras só existem se a mais
           * fina já for visível.
           */
          .setAlpha(0.62 + tier * 0.13)
          /* Roda devagar: uma casca parada lê-se como um autocolante. */
          .setRotation(nowMs / 3_600);
      }
    }

    /*
     * **A ESTRELA da paralisia total**, acima da barra e do nome.
     *
     * Só quando o ataque também está travado. É o único destes sinais que sobe
     * acima da barra, e merece-o: ele é o único que muda o que o adversário pode
     * fazer contra quem o leva.
     */
    if (this.paralysis) {
      if (!state.paralysed) {
        this.paralysis.setVisible(false);
      } else {
        const star = state.hullRadius * 2.4 * (1 + Math.sin(nowMs / 240) * 0.08);
        /* Ela entra na pilha como as outras: ver `stack`. */
        this.paralysis
          .setVisible(true)
          .setPosition(state.x, this.stack(star))
          .setDisplaySize(star, star)
          .setTint(ICE_TINT)
          .setAlpha(0.9)
          .setRotation(nowMs / 1_800);
      }
    }

    /*
     * **A LENTIDÃO**: flocos leves e espaçados, deixados no MUNDO.
     *
     * Por tempo e não por quadro, como todos os rastos deste projecto - senão um
     * monitor a 144 Hz faria neve a dobrar. Eles caem devagar do casco e apagam;
     * não há casca nem estrela, porque quem está lento continua livre para andar
     * e atacar, e o sinal tem de dizer isso.
     */
    /*
     * **O CURTO-CIRCUITO**: arcos a crepitar à volta do casco (gate V28H.X).
     *
     * À volta e não por cima, e é a diferença que o separa da casca de gelo:
     * aquela é uma coroa fechada de cristais que diz "preso", e isto são três
     * arcos soltos que saltam e mudam de sítio - a leitura de "avariado".
     *
     * O crepitar é feito com o RELÓGIO e não com objectos novos: a textura roda
     * depressa e a opacidade salta em degraus, portanto três cascos em curto num
     * 3x3 custam três sprites e nada mais.
     */
    if (this.shock) {
      if (!state.attackBroken) {
        this.shock.setVisible(false);
      } else {
        /*
         * **Sobre o CASCO, e não à volta dele** (gate V28H.X).
         *
         * Pouco mais do que o diâmetro do casco: os riscos vivem dentro do
         * disco da textura, portanto desenhá-la à medida da nave é o que os põe
         * a atravessá-la. Um vão maior punha-os outra vez a contornar.
         */
        const span = state.hullRadius * 2.15;
        /* A chapa muda, e é a mudança que faz a descarga saltar. */
        const frame = Math.floor(nowMs / SHOCK_FRAME_MS) % SHOCK_VARIANTS;
        const key = shockTextureKey(frame);
        if (this.shock.texture.key !== key) {
          this.shock.setTexture(key);
        }
        this.shock
          .setVisible(true)
          .setPosition(state.x, state.y)
          .setDisplaySize(span, span)
          .setTint(SHOCK_TINT)
          /* Degraus e não uma onda: uma faísca acende e apaga, não respira. */
          .setAlpha(frame === 0 ? 0.95 : frame === 1 ? 0.62 : 0.85)
          /*
           * Uma volta por chapa, e não um giro contínuo: a rotação existe para
           * as doze posições não se repetirem sempre no mesmo sítio do casco.
           */
          .setRotation(Math.floor(nowMs / SHOCK_FRAME_MS) * 0.9);
      }
    }

    /*
     * **A MARCA DA CAVEIRA**, acima do nickname (decisão do dono, V28H.X).
     *
     * Estava sobre o casco, e o argumento parecia bom - uma marca é uma mira, e
     * uma mira aponta. O dono viu-a lá e decidiu o contrário: ela sobe para a
     * faixa de cima, com as outras, onde não disputa a nave com a arte dela nem
     * com as gotas do Veneno e as garras do Sangramento.
     *
     * Entra na PILHA como o selo do Malware, portanto três marcas ao mesmo tempo
     * empilham-se em vez de se sobreporem.
     *
     * Maior do que era, e néon: um halo da mesma cor por trás dela, somado. É o
     * mesmo truque da chapa do Leviatã - a cópia somada só acrescenta onde já há
     * luz - e é o que a faz arder em vez de estar apenas desenhada.
     */
    if (this.skull && this.skullGlow) {
      if (!state.marked) {
        this.skull.setVisible(false);
        this.skullGlow.setVisible(false);
      } else {
        /* **O dobro** (decisão do dono, gate V28H.X): ela vive contra o céu. */
        const bone = state.hullRadius * 2.9;
        const pulse = 1 + Math.sin(nowMs / 420) * 0.05;
        const at = this.stack(bone);
        this.skullGlow
          .setVisible(true)
          .setPosition(state.x, at)
          .setDisplaySize(bone * 1.9 * pulse, bone * 1.9 * pulse)
          .setTint(SKULL_TINT)
          .setAlpha(0.42 + Math.sin(nowMs / 420) * 0.14);
        this.skull
          .setVisible(true)
          .setPosition(state.x, at)
          .setDisplaySize(bone * pulse, bone * pulse)
          .setTint(SKULL_TINT)
          .setAlpha(0.95);
      }
    }

    if (state.slowed && nowMs - this.lastFlakeAt >= FLAKE_INTERVAL_MS) {
      this.lastFlakeAt = nowMs;
      const seed = this.nextFlake;
      const angle = hash(seed) * Math.PI * 2;
      const distance = state.hullRadius * (0.4 + hash(seed * 3 + 1) * 0.7);
      this.snow[seed % FLAKES] = {
        x: state.x + Math.cos(angle) * distance,
        y: state.y + Math.sin(angle) * distance,
        bornAt: nowMs,
        /* Flocos três vezes maiores, pela mesma decisão do dono. */
        size: state.hullRadius * (0.42 + hash(seed * 7 + 2) * 0.36),
      };
      this.nextFlake = (this.nextFlake + 1) % FLAKES;
    }
    this.advanceSnow(nowMs);
  }

  /** Os flocos descem devagar e apagam. Nada nasce aqui. */
  private advanceSnow(nowMs: number): void {
    for (const [index, sprite] of this.flakes.entries()) {
      const flake = this.snow[index];
      if (flake === undefined) {
        sprite.setVisible(false);
        continue;
      }
      const life = (nowMs - flake.bornAt) / FLAKE_LIFE_MS;
      if (life >= 1 || life < 0) {
        sprite.setVisible(false);
        continue;
      }
      sprite
        .setVisible(true)
        .setPosition(flake.x, flake.y + life * flake.size * 2.2)
        .setDisplaySize(flake.size, flake.size)
        .setTint(ICE_TINT)
        .setAlpha((1 - life) * 0.85)
        .setRotation(life * 1.6 + index);
    }
  }

  private hide(): void {
    this.shock?.setVisible(false);
    this.skull?.setVisible(false);
    this.skullGlow?.setVisible(false);
    this.malware?.setVisible(false);
    this.ghost?.setVisible(false);
    this.aura?.setVisible(false);
    this.circle?.setVisible(false);
    this.frost?.setVisible(false);
    this.paralysis?.setVisible(false);
  }

  private build(): void {
    if (this.malware !== null) {
      return;
    }
    ensureStatusTextures(this.scene);
    const layer = (key: string, depth: number) =>
      this.scene.add.image(0, 0, key).setDepth(depth).setBlendMode(this.blendAdd).setVisible(false);
    /* A aura por baixo do casco; o resto por cima dele, sem chegar à barra. */
    /* Os dois do CHÃO, por baixo do casco: o selo e o halo que o acompanha. */
    this.circle = layer(TEXTURE.malwareCircle, this.groundDepth);
    this.aura = layer(TEXTURE.malwareAura, this.groundDepth + 1);
    this.frost = layer(TEXTURE.frost, this.depth);
    /* Estes três, acima da barra e do nickname: eles são para ser lidos. */
    this.ghost = layer(TEXTURE.malware, this.barDepth);
    this.malware = layer(TEXTURE.malware, this.barDepth + 1);
    this.paralysis = layer(TEXTURE.paralysis, this.barDepth + 2);
    /* O arco à volta do casco, e a caveira por cima dele. */
    this.shock = layer(shockTextureKey(0), this.depth);
    /* O halo por trás, e a caveira por cima dele: os dois acima da barra. */
    this.skullGlow = layer(TEXTURE.glow, this.barDepth + 3);
    this.skullGlow.setBlendMode(this.blendAdd);
    this.skull = layer(TEXTURE.skull, this.barDepth + 4);
    for (let index = 0; index < FLAKES; index += 1) {
      this.flakes.push(layer(TEXTURE.flake, this.depth));
    }
  }

  /** Larga tudo. Para o `SHUTDOWN` da cena e para a saída de um participante. */
  destroy(): void {
    this.malware?.destroy();
    this.malware = null;
    this.ghost?.destroy();
    this.ghost = null;
    this.aura?.destroy();
    this.aura = null;
    this.circle?.destroy();
    this.circle = null;
    this.shock?.destroy();
    this.shock = null;
    this.skull?.destroy();
    this.skull = null;
    this.skullGlow?.destroy();
    this.skullGlow = null;
    this.frost?.destroy();
    this.frost = null;
    this.paralysis?.destroy();
    this.paralysis = null;
    for (const flake of this.flakes) {
      flake.destroy();
    }
    this.flakes = [];
    this.snow.length = 0;
  }
}

/**
 * **O hack no casco de quem LANÇOU o Malware** (gate V28H.W).
 *
 * Curto - o contrato pede "símbolos, segmentos ou pulsos digitais breves" - e do
 * lançador, não da vítima: são duas coisas diferentes, e a vítima tem o símbolo
 * persistente que a classe acima desenha.
 *
 * Três anéis quadrados que abrem e apagam, em magenta e ciano alternados. Sem
 * textura nova: eles são rectângulos vazados, que é o que um pulso de dados
 * parece e o que distingue isto de qualquer onda redonda já no jogo.
 */
/**
 * **O que sai da nave que MARCA** (decisão do dono, gate V28H.X).
 *
 * A Marca da Caveira não tem projéctil, não carrega e não deixa nada no casco de
 * quem a dá: do lado de quem lança, ela era invisível. "Não há nenhum efeito que
 * sai da nave que disparou o efeito, precisa ter."
 *
 * O que ela desenha são duas coisas ao mesmo tempo, e as duas curtas:
 *
 * - **três anéis vermelhos** a abrir do casco, cada um com o seu atraso - é a
 *   ordem a sair;
 * - **a própria caveira**, a nascer sobre quem lançou e a subir enquanto apaga -
 *   é a marca a ser cunhada, antes de ir parar ao adversário.
 *
 * Uma vez por lançamento: repetir enquanto o pulso corre não o reinicia, porque
 * a letra que o dispara fica acesa os seis segundos do debuff.
 */
export class MarkCastFx {
  private readonly scene: Phaser.Scene;
  private readonly depth: number;
  private rings: Phaser.GameObjects.Arc[] = [];
  private stamp: Phaser.GameObjects.Image | null = null;
  private startedAt: number | null = null;
  /** O estado da carta no quadro anterior: é a transição que dispara. */
  private wasActive = false;

  constructor(scene: Phaser.Scene, depth: number) {
    this.scene = scene;
    this.depth = depth;
  }

  /**
   * **Arranca no FLANCO, e não enquanto a carta estiver acesa** (V28H.X).
   *
   * A letra fica acesa os seis segundos do debuff, e um pulso que reagisse ao
   * NÍVEL dela reacendia-se assim que acabava - o dono viu-o: "ela está pulsando
   * por muito tempo, é só para sinalizar o uso da habilidade".
   *
   * Reagir à transição resolve-o de uma vez: o pulso toca quando a carta passa
   * de apagada a acesa, corre os seus dois segundos, e não volta a tocar até ela
   * se apagar e ser usada outra vez.
   */
  sync(active: boolean, nowMs: number): void {
    if (active && !this.wasActive) {
      this.startedAt = nowMs;
    }
    this.wasActive = active;
  }

  update(x: number, y: number, hullRadius: number, nowMs: number): void {
    if (this.startedAt === null) {
      return;
    }
    const life = (nowMs - this.startedAt) / MARK_CAST_MS;
    if (life >= 1) {
      for (const ring of this.rings) {
        ring.setVisible(false);
      }
      this.stamp?.setVisible(false);
      this.startedAt = null;
      return;
    }
    this.build();
    for (const [index, ring] of this.rings.entries()) {
      /* Escalonados: o segundo abre quando o primeiro vai a meio. */
      const local = life * 1.7 - index * 0.28;
      if (local <= 0 || local >= 1) {
        ring.setVisible(false);
        continue;
      }
      /*
       * Os anéis nascem JÁ FORA do casco e abrem para longe dele: por dentro
       * eles cruzavam a nave, e um sinal que tapa aquilo que assinala não
       * assinala nada.
       */
      ring
        .setVisible(true)
        .setPosition(x, y)
        .setRadius(hullRadius * (1.5 + local * 2.6))
        .setStrokeStyle(2 + (1 - local) * 5, SKULL_TINT, (1 - local) * 0.95);
    }
    /* E a caveira a ser cunhada: cresce, sobe e apaga. */
    if (this.stamp) {
      /* Acima do casco desde o princípio, e a subir: nunca por cima dele. */
      const grow = hullRadius * (1.1 + life * 0.9);
      this.stamp
        .setVisible(true)
        .setPosition(x, y - hullRadius * (1.7 + life * 1.4))
        .setDisplaySize(grow, grow)
        .setTint(SKULL_TINT)
        .setAlpha((1 - life) * 0.95);
    }
  }

  private build(): void {
    if (this.rings.length > 0) {
      return;
    }
    ensureStatusTextures(this.scene);
    for (let index = 0; index < 3; index += 1) {
      this.rings.push(
        this.scene.add.circle(0, 0, 1).setFillStyle().setDepth(this.depth).setVisible(false),
      );
    }
    this.stamp = this.scene.add
      .image(0, 0, TEXTURE.skull)
      .setDepth(this.depth + 1)
      .setVisible(false);
  }

  destroy(): void {
    for (const ring of this.rings) {
      ring.destroy();
    }
    this.rings = [];
    this.stamp?.destroy();
    this.stamp = null;
    this.startedAt = null;
  }
}

/**
 * **Dois segundos** (decisão do dono, gate V28H.X): o pulso da Marca.
 *
 * Ele assinala o USO da carta, e não a duração dela - a carta dura seis
 * segundos, e quem os conta é a caveira sobre o adversário.
 */
const MARK_CAST_MS = 2_000;

export class MalwareCastFx {
  private readonly scene: Phaser.Scene;
  private readonly depth: number;
  private rings: Phaser.GameObjects.Rectangle[] = [];
  private startedAt: number | null = null;

  constructor(scene: Phaser.Scene, depth: number) {
    this.scene = scene;
    this.depth = depth;
  }

  /** Dispara o pulso. Repetir enquanto ele corre não o reinicia. */
  fire(nowMs: number): void {
    if (this.startedAt !== null && nowMs - this.startedAt < MALWARE_CAST_MS) {
      return;
    }
    this.startedAt = nowMs;
  }

  update(x: number, y: number, hullRadius: number, nowMs: number): void {
    if (this.startedAt === null) {
      return;
    }
    const life = (nowMs - this.startedAt) / MALWARE_CAST_MS;
    if (life >= 1) {
      for (const ring of this.rings) {
        ring.setVisible(false);
      }
      this.startedAt = null;
      return;
    }
    this.build();
    for (const [index, ring] of this.rings.entries()) {
      /* Escalonados: o segundo começa quando o primeiro vai a meio. */
      const local = life * 1.6 - index * 0.3;
      if (local <= 0 || local >= 1) {
        ring.setVisible(false);
        continue;
      }
      const span = hullRadius * (0.9 + local * 2.4);
      /*
       * **Três anéis, três cores da mesma família** (gate V28H.W): roxo, ciano e
       * magenta. O roxo é a cor da carta - o mesmo do selo que vai aparecer do
       * outro lado - e os outros dois são o desvio de canal a acompanhá-lo,
       * portanto quem lança e quem leva mostram a mesma assinatura.
       *
       * A linha ENGROSSA com a distância em vez de afinar, que é o contrário do
       * que uma onda faz e o que faz isto ler-se como um sinal a ser emitido.
       */
      const colour =
        index % 3 === 0 ? MALWARE_VIOLET : index % 3 === 1 ? MALWARE_CYAN : MALWARE_MAGENTA;
      ring
        .setVisible(true)
        .setPosition(x, y)
        .setSize(span, span)
        .setStrokeStyle(2 + local * 3, colour, (1 - local) * 0.95)
        .setRotation(local * 0.8 + index);
    }
  }

  private build(): void {
    if (this.rings.length > 0) {
      return;
    }
    for (let index = 0; index < 3; index += 1) {
      this.rings.push(
        this.scene.add.rectangle(0, 0, 1, 1).setFillStyle().setDepth(this.depth).setVisible(false),
      );
    }
  }

  destroy(): void {
    for (const ring of this.rings) {
      ring.destroy();
    }
    this.rings = [];
    this.startedAt = null;
  }
}

/** Quanto tempo o pulso de lançamento do Malware dura. Breve, por contrato. */
const MALWARE_CAST_MS = 620;
